import {
  Alert,
  AlertVariant,
  ButtonVariant,
  Divider,
  DropdownItem,
  Label,
  PageSection,
  Tab,
  TabTitleText,
  Tooltip
} from "../_snowpack/pkg/@patternfly/react-core.js";
import {InfoCircleIcon} from "../_snowpack/pkg/@patternfly/react-icons.js";
import {cloneDeep, sortBy} from "../_snowpack/pkg/lodash-es.js";
import React, {useMemo, useState} from "../_snowpack/pkg/react.js";
import {Controller, FormProvider, useForm, useWatch} from "../_snowpack/pkg/react-hook-form.js";
import {useTranslation} from "../_snowpack/pkg/react-i18next.js";
import {useHistory, useParams} from "../_snowpack/pkg/react-router-dom.js";
import {useAlerts} from "../components/alert/Alerts.js";
import {
  ConfirmDialogModal,
  useConfirmDialog
} from "../components/confirm-dialog/ConfirmDialog.js";
import {DownloadDialog} from "../components/download-dialog/DownloadDialog.js";
import {
  ViewHeader
} from "../components/view-header/ViewHeader.js";
import {KeycloakSpinner} from "../components/keycloak-spinner/KeycloakSpinner.js";
import {useAdminClient, useFetch} from "../context/auth/AdminClient.js";
import {useRealm} from "../context/realm-context/RealmContext.js";
import {RolesList} from "../realm-roles/RolesList.js";
import {
  convertFormValuesToObject,
  convertToFormValues,
  exportClient
} from "../util.js";
import useToggle from "../utils/useToggle.js";
import {AdvancedTab} from "./AdvancedTab.js";
import {ClientSettings} from "./ClientSettings.js";
import {Credentials} from "./credentials/Credentials.js";
import {Keys} from "./keys/Keys.js";
import {toClient} from "./routes/Client.js";
import {toClients} from "./routes/Clients.js";
import {ClientScopes} from "./scopes/ClientScopes.js";
import {EvaluateScopes} from "./scopes/EvaluateScopes.js";
import {ServiceAccount} from "./service-account/ServiceAccount.js";
import {isRealmClient, getProtocolName} from "./utils.js";
import {SamlKeys} from "./keys/SamlKeys.js";
import {MapperList} from "../client-scopes/details/MapperList.js";
import {toMapper} from "./routes/Mapper.js";
import {AuthorizationSettings} from "./authorization/Settings.js";
import {AuthorizationResources} from "./authorization/Resources.js";
import {AuthorizationScopes} from "./authorization/Scopes.js";
import {AuthorizationPolicies} from "./authorization/Policies.js";
import {AuthorizationPermissions} from "./authorization/Permissions.js";
import {AuthorizationEvaluate} from "./authorization/AuthorizationEvaluate.js";
import {
  routableTab,
  RoutableTabs
} from "../components/routable-tabs/RoutableTabs.js";
import {
  toAuthorizationTab
} from "./routes/AuthenticationTab.js";
import {toClientScopesTab} from "./routes/ClientScopeTab.js";
import {AuthorizationExport} from "./authorization/AuthorizationExport.js";
const ClientDetailHeader = ({
  onChange,
  value,
  save,
  client,
  toggleDownloadDialog,
  toggleDeleteDialog
}) => {
  const {t} = useTranslation("clients");
  const [toggleDisableDialog, DisableConfirm] = useConfirmDialog({
    titleKey: "clients:disableConfirmTitle",
    messageKey: "clients:disableConfirm",
    continueButtonLabel: "common:disable",
    onConfirm: () => {
      onChange(!value);
      save();
    }
  });
  const badges = useMemo(() => {
    const protocolName = getProtocolName(t, client.protocol ?? "openid-connect");
    const text = client.bearerOnly ? /* @__PURE__ */ React.createElement(Tooltip, {
      "data-testid": "bearer-only-explainer-tooltip",
      content: t("explainBearerOnly")
    }, /* @__PURE__ */ React.createElement(Label, {
      "data-testid": "bearer-only-explainer-label",
      icon: /* @__PURE__ */ React.createElement(InfoCircleIcon, null)
    }, protocolName)) : /* @__PURE__ */ React.createElement(Label, null, protocolName);
    return [{text}];
  }, [client, t]);
  const dropdownItems = [
    /* @__PURE__ */ React.createElement(DropdownItem, {
      key: "download",
      onClick: toggleDownloadDialog
    }, t("downloadAdapterConfig")),
    /* @__PURE__ */ React.createElement(DropdownItem, {
      key: "export",
      onClick: () => exportClient(client)
    }, t("common:export")),
    ...!isRealmClient(client) ? [
      /* @__PURE__ */ React.createElement(Divider, {
        key: "divider"
      }),
      /* @__PURE__ */ React.createElement(DropdownItem, {
        "data-testid": "delete-client",
        key: "delete",
        onClick: toggleDeleteDialog
      }, t("common:delete"))
    ] : []
  ];
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(DisableConfirm, null), /* @__PURE__ */ React.createElement(ViewHeader, {
    titleKey: client.clientId,
    subKey: "clients:clientsExplain",
    badges,
    divider: false,
    helpTextKey: "clients-help:enableDisable",
    dropdownItems,
    isEnabled: value,
    onToggle: (value2) => {
      if (!value2) {
        toggleDisableDialog();
      } else {
        onChange(value2);
        save();
      }
    }
  }));
};
export default function ClientDetails() {
  const {t} = useTranslation("clients");
  const adminClient = useAdminClient();
  const {addAlert, addError} = useAlerts();
  const {realm} = useRealm();
  const history = useHistory();
  const [downloadDialogOpen, toggleDownloadDialogOpen] = useToggle();
  const [changeAuthenticatorOpen, toggleChangeAuthenticatorOpen] = useToggle();
  const form = useForm({shouldUnregister: false});
  const {clientId} = useParams();
  const clientAuthenticatorType = useWatch({
    control: form.control,
    name: "clientAuthenticatorType",
    defaultValue: "client-secret"
  });
  const [client, setClient] = useState();
  const [clients, setClients] = useState([]);
  const [clientRoles, setClientRoles] = useState([]);
  const [users, setUsers] = useState([]);
  useFetch(() => Promise.all([
    adminClient.clients.find(),
    adminClient.roles.find(),
    adminClient.users.find()
  ]), ([clients2, roles, users2]) => {
    setClients(clients2);
    setClientRoles(roles);
    setUsers(users2);
  }, []);
  const loader = async () => {
    const roles = await adminClient.clients.listRoles({id: clientId});
    return sortBy(roles, (role) => role.name?.toUpperCase());
  };
  const [toggleDeleteDialog, DeleteConfirm] = useConfirmDialog({
    titleKey: "clients:clientDeleteConfirmTitle",
    messageKey: "clients:clientDeleteConfirm",
    continueButtonLabel: "common:delete",
    continueButtonVariant: ButtonVariant.danger,
    onConfirm: async () => {
      try {
        await adminClient.clients.del({id: clientId});
        addAlert(t("clientDeletedSuccess"), AlertVariant.success);
        history.push(toClients({realm}));
      } catch (error) {
        addError("clients:clientDeleteError", error);
      }
    }
  });
  const setupForm = (client2) => {
    convertToFormValues(client2, form.setValue, ["redirectUris", "webOrigins"]);
  };
  useFetch(() => adminClient.clients.findOne({id: clientId}), (fetchedClient) => {
    if (!fetchedClient) {
      throw new Error(t("common:notFound"));
    }
    setClient(cloneDeep(fetchedClient));
    setupForm(fetchedClient);
  }, [clientId]);
  const save = async ({confirmed = false, messageKey = "clientSaveSuccess"} = {
    confirmed: false,
    messageKey: "clientSaveSuccess"
  }) => {
    if (await form.trigger()) {
      if (!client?.publicClient && client?.clientAuthenticatorType !== clientAuthenticatorType && !confirmed) {
        toggleChangeAuthenticatorOpen();
        return;
      }
      const submittedClient = convertFormValuesToObject(form.getValues(), [
        "redirectUris",
        "webOrigins"
      ]);
      try {
        const newClient = {
          ...client,
          ...submittedClient
        };
        newClient.clientId = newClient.clientId?.trim();
        await adminClient.clients.update({id: clientId}, newClient);
        setupForm(newClient);
        setClient(newClient);
        addAlert(t(messageKey), AlertVariant.success);
      } catch (error) {
        addError("clients:clientSaveError", error);
      }
    }
  };
  const addMappers = async (mappers) => {
    if (!Array.isArray(mappers)) {
      const mapper = mappers;
      history.push(toMapper({
        realm,
        id: client.id,
        mapperId: mapper.id
      }));
    } else {
      try {
        await adminClient.clients.addMultipleProtocolMappers({id: client.id}, mappers);
        setClient(await adminClient.clients.findOne({id: client.id}));
        addAlert(t("common:mappingCreatedSuccess"), AlertVariant.success);
      } catch (error) {
        addError("common:mappingCreatedError", error);
      }
    }
  };
  const onDeleteMapper = async (mapper) => {
    try {
      await adminClient.clients.delProtocolMapper({
        id: client.id,
        mapperId: mapper.id
      });
      setClient({
        ...client,
        protocolMappers: client?.protocolMappers?.filter((m) => m.id !== mapper.id)
      });
      addAlert(t("common:mappingDeletedSuccess"), AlertVariant.success);
    } catch (error) {
      addError("common:mappingDeletedError", error);
    }
    return true;
  };
  if (!client) {
    return /* @__PURE__ */ React.createElement(KeycloakSpinner, null);
  }
  const route = (tab) => routableTab({
    to: toClient({
      realm,
      clientId,
      tab
    }),
    history
  });
  const authenticationRoute = (tab) => routableTab({
    to: toAuthorizationTab({
      realm,
      clientId,
      tab
    }),
    history
  });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ConfirmDialogModal, {
    continueButtonLabel: "common:yes",
    titleKey: t("changeAuthenticatorConfirmTitle", {
      clientAuthenticatorType
    }),
    open: changeAuthenticatorOpen,
    toggleDialog: toggleChangeAuthenticatorOpen,
    onConfirm: () => save({confirmed: true})
  }, /* @__PURE__ */ React.createElement(React.Fragment, null, t("changeAuthenticatorConfirm", {
    clientAuthenticatorType
  }), clientAuthenticatorType === "client-jwt" && /* @__PURE__ */ React.createElement(Alert, {
    variant: "info",
    isInline: true,
    title: t("signedJWTConfirm")
  }))), /* @__PURE__ */ React.createElement(DeleteConfirm, null), /* @__PURE__ */ React.createElement(DownloadDialog, {
    id: client.id,
    protocol: client.protocol,
    open: downloadDialogOpen,
    toggleDialog: toggleDownloadDialogOpen
  }), /* @__PURE__ */ React.createElement(Controller, {
    name: "enabled",
    control: form.control,
    defaultValue: true,
    render: ({onChange, value}) => /* @__PURE__ */ React.createElement(ClientDetailHeader, {
      value,
      onChange,
      client,
      save,
      toggleDeleteDialog,
      toggleDownloadDialog: toggleDownloadDialogOpen
    })
  }), /* @__PURE__ */ React.createElement(PageSection, {
    variant: "light",
    className: "pf-u-p-0"
  }, /* @__PURE__ */ React.createElement(FormProvider, {
    ...form
  }, /* @__PURE__ */ React.createElement(RoutableTabs, {
    "data-testid": "client-tabs",
    isBox: true,
    mountOnEnter: true
  }, /* @__PURE__ */ React.createElement(Tab, {
    id: "settings",
    "data-testid": "clientSettingsTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("common:settings")),
    ...route("settings")
  }, /* @__PURE__ */ React.createElement(ClientSettings, {
    client,
    save: () => save(),
    reset: () => setupForm(client)
  })), (!client.publicClient && !isRealmClient(client) || client.protocol === "saml") && /* @__PURE__ */ React.createElement(Tab, {
    id: "keys",
    "data-testid": "keysTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("keys")),
    ...route("keys")
  }, client.protocol === "openid-connect" && /* @__PURE__ */ React.createElement(Keys, {
    clientId,
    save
  }), client.protocol === "saml" && /* @__PURE__ */ React.createElement(SamlKeys, {
    clientId,
    save
  })), !client.publicClient && !isRealmClient(client) && /* @__PURE__ */ React.createElement(Tab, {
    id: "credentials",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("credentials")),
    ...route("credentials")
  }, /* @__PURE__ */ React.createElement(Credentials, {
    clientId,
    save: () => save()
  })), !isRealmClient(client) && /* @__PURE__ */ React.createElement(Tab, {
    id: "mappers",
    "data-testid": "mappersTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("mappers")),
    ...route("mappers")
  }, /* @__PURE__ */ React.createElement(MapperList, {
    model: client,
    onAdd: addMappers,
    onDelete: onDeleteMapper,
    detailLink: (mapperId) => toMapper({realm, id: client.id, mapperId})
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "roles",
    "data-testid": "rolesTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("roles")),
    ...route("roles")
  }, /* @__PURE__ */ React.createElement(RolesList, {
    loader,
    paginated: false,
    messageBundle: "clients"
  })), !isRealmClient(client) && /* @__PURE__ */ React.createElement(Tab, {
    id: "clientScopes",
    "data-testid": "clientScopesTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("clientScopes")),
    ...route("clientScopes")
  }, /* @__PURE__ */ React.createElement(RoutableTabs, {
    defaultLocation: toClientScopesTab({
      realm,
      clientId,
      tab: "setup"
    })
  }, /* @__PURE__ */ React.createElement(Tab, {
    id: "setup",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("setup")),
    ...routableTab({
      to: toClientScopesTab({
        realm,
        clientId,
        tab: "setup"
      }),
      history
    })
  }, /* @__PURE__ */ React.createElement(ClientScopes, {
    clientName: client.clientId,
    clientId,
    protocol: client.protocol
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "evaluate",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("evaluate")),
    ...routableTab({
      to: toClientScopesTab({
        realm,
        clientId,
        tab: "evaluate"
      }),
      history
    })
  }, /* @__PURE__ */ React.createElement(EvaluateScopes, {
    clientId,
    protocol: client.protocol
  })))), client.serviceAccountsEnabled && /* @__PURE__ */ React.createElement(Tab, {
    id: "authorization",
    "data-testid": "authorizationTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("authorization")),
    ...route("authorization")
  }, /* @__PURE__ */ React.createElement(RoutableTabs, {
    mountOnEnter: true,
    unmountOnExit: true,
    defaultLocation: toAuthorizationTab({
      realm,
      clientId,
      tab: "settings"
    })
  }, /* @__PURE__ */ React.createElement(Tab, {
    id: "settings",
    "data-testid": "authorizationSettings",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("settings")),
    ...authenticationRoute("settings")
  }, /* @__PURE__ */ React.createElement(AuthorizationSettings, {
    clientId
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "resources",
    "data-testid": "authorizationResources",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("resources")),
    ...authenticationRoute("resources")
  }, /* @__PURE__ */ React.createElement(AuthorizationResources, {
    clientId
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "scopes",
    "data-testid": "authorizationScopes",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("scopes")),
    ...authenticationRoute("scopes")
  }, /* @__PURE__ */ React.createElement(AuthorizationScopes, {
    clientId
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "policies",
    "data-testid": "authorizationPolicies",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("policies")),
    ...authenticationRoute("policies")
  }, /* @__PURE__ */ React.createElement(AuthorizationPolicies, {
    clientId
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "permissions",
    "data-testid": "authorizationPermissions",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("permissions")),
    ...authenticationRoute("permissions")
  }, /* @__PURE__ */ React.createElement(AuthorizationPermissions, {
    clientId
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "evaluate",
    "data-testid": "authorizationEvaluate",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("evaluate")),
    ...authenticationRoute("evaluate")
  }, /* @__PURE__ */ React.createElement(AuthorizationEvaluate, {
    clients,
    clientName: client.clientId,
    clientRoles,
    users,
    save,
    reset: () => setupForm(client)
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "export",
    "data-testid": "authorizationExport",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("common:export")),
    ...authenticationRoute("export")
  }, /* @__PURE__ */ React.createElement(AuthorizationExport, null)))), client.serviceAccountsEnabled && /* @__PURE__ */ React.createElement(Tab, {
    id: "serviceAccount",
    "data-testid": "serviceAccountTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("serviceAccount")),
    ...route("serviceAccount")
  }, /* @__PURE__ */ React.createElement(ServiceAccount, {
    client
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "advanced",
    "data-testid": "advancedTab",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("advanced")),
    ...route("advanced")
  }, /* @__PURE__ */ React.createElement(AdvancedTab, {
    save,
    client
  }))))));
}
